/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/helpers';
import {DevicePartNumber, DevicePartNumbers} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState, PageInitializeAction} from '@/types/redux';
import {gridDefaultPageSize} from '../../../utils/helpers';

// Reducer Actions
export const REDUCER_NAME                              = 'devicePartNumbers';
export const INITIALIZE_DEVICE_PART_NUMBERS            = 'INITIALIZE_DEVICE_PART_NUMBERS';
export const SET_GRID_COLUMNS                          = 'SET_DEVICE_PART_NUMBERS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                       = 'UPDATE_DEVICE_PART_NUMBERS_GRID_COLUMNS';
export const DEVICE_PART_NUMBERS_GRID_REFRESHED        = 'DEVICE_PART_NUMBERS_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE                        = 'SET_DEVICE_PART_NUMBERS_GRID_PAGE_SIZE';
export const UPDATE_DEVICE_PART_NUMBERS_SEARCH_QUERIES = 'UPDATE_DEVICE_PART_NUMBERS_SEARCH_QUERIES';
export const RESET_DEVICE_PART_NUMBERS_SEARCH_QUERIES  = 'RESET_DEVICE_PART_NUMBERS_SEARCH_QUERIES';
export const ADD_DEVICE_PART_NUMBERS                   = 'ADD_DEVICE_PART_NUMBERS';
export const SHOW_DEVICE_PART_NUMBERS_DIALOG           = 'SHOW_DEVICE_PART_NUMBERS_DIALOG';
export const DELETE_DEVICE_PART_NUMBERS                = 'DELETE_DEVICE_PART_NUMBERS';

/**
 * initialize DevicePartNumbers
 */
export const initializeDevicePartNumbers = (): PageInitializeAction => {
  return {type: INITIALIZE_DEVICE_PART_NUMBERS};
};

/**
 * Invoked when Grid Refreshed
 */
export const devicePartNumbersGridRefreshed = (): PageInitializeAction => {
  return {type: DEVICE_PART_NUMBERS_GRID_REFRESHED};
};

/**
 * @addDevicePartNumber
 *
 * Invoked when the Add Device Part Number file editor is to be displayed or removed.
 */
export const addDevicePartNumbers = (show = true) => ({
  type : ADD_DEVICE_PART_NUMBERS, show
});

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setDevicePartNumbersGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

// Updates the modular search queries for Device Part Numbers
export const updateDevicePartNumbersSearchQueries = (searchParams: DevicePartNumber) => ({
  type : UPDATE_DEVICE_PART_NUMBERS_SEARCH_QUERIES, searchParams
});

// Resets the Device Part Numbers filters state
export const resetDevicePartNumbersSearchQueries = () => ({
  type : RESET_DEVICE_PART_NUMBERS_SEARCH_QUERIES
});

/**
 * @showDevicePartNumber
 *
 * Invoked when the Edit/View Device Part Number file editor is to be displayed or removed.
 */
export const showDevicePartNumbers = (
  devicePartNumbers: DevicePartNumber | DevicePartNumbers,
  showDialog: boolean = true,
  isView: boolean = false) => ({
  type : SHOW_DEVICE_PART_NUMBERS_DIALOG, devicePartNumbers, showDialog, isView
});

/**
 * Invoked when the confirm delete devicePartNumbers Dialog is to be displayed or removed.
 */
export const showDeleteDevicePartNumberDialog = (
  devicePartNumbers: DevicePartNumber, show = true
): DevicePartNumbersAction => ({
  type : DELETE_DEVICE_PART_NUMBERS, devicePartNumbers, showDialog : show
});

/**
 * REDUCERS
 */
export interface DevicePartNumbersAction extends AdminAction {
  devicePartNumbers?: DevicePartNumber;
  showDialog?: boolean;
  isView?: boolean;
};

interface DevicePartNumbersActionState extends AsyncState {
  selected: DevicePartNumber | DevicePartNumbers | null;
  result: DevicePartNumber | DevicePartNumbers | null;
  record?: DevicePartNumber | DevicePartNumbers | null;
  showDialog: boolean;
  isView?: boolean;
  id?: number;
};

export interface DevicePartNumbersState extends PageState {
  devicePartNumbers: {
    error: ServiceError;
    loading: boolean;
  };
  grid: {
    columnState: ColumnState[];
    preferedColumnState: ColumnState[];
    hasUpdate: boolean;
    includeSubGroups: boolean;
    pageSize: number;
  };
  add: DevicePartNumbersActionState;
  edit: DevicePartNumbersActionState;
  search: DevicePartNumber;
  destroy: DevicePartNumbersActionState;
};

const clearSearch = (): DevicePartNumber => ({
  devicePartNum           : null,
  deviceType              : null,
  deviceConfigurationName : null,
  carrier                 : null,
  isThirdParty            : null
});

// defines the user preference key for the device part numbers page ...
export const PREFERENCE = 'devicePartNumbers';

// defines the initial state for the reducer ...
export const initialState: DevicePartNumbersState = {
  isInitialized     : false,
  devicePartNumbers : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  add : {
    showDialog : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null
  },
  edit : {
    showDialog : false,
    isView     : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    id         : null
  },
  destroy : {
    showDialog : false,
    loading    : false,
    selected   : null,
    error      : null,
    result     : null
  },
  search : clearSearch()
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_DEVICE_PART_NUMBERS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, devicePartNumbers}: DevicePartNumbersState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : devicePartNumbers.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: DevicePartNumbersState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: DevicePartNumbersState, {pageSize}) => {
    grid.pageSize = pageSize;
  },
  [ADD_DEVICE_PART_NUMBERS] : (
    {add}: DevicePartNumbersState,
    {devicePartNumbers, show}: DevicePartNumbersAction
  ) => {
    add.showDialog = show;
    add.result     = devicePartNumbers;
    add.selected   = null;
    add.loading    = true;
  },
  [SHOW_DEVICE_PART_NUMBERS_DIALOG] : (
    {edit}: DevicePartNumbersState,
    {devicePartNumbers, showDialog, isView}: DevicePartNumbersAction
  ) => {
    edit.showDialog = showDialog;
    edit.result     = null;
    edit.selected   = showDialog ? devicePartNumbers : null;
    edit.loading    = true;
    edit.isView     = isView;
    edit.id         = devicePartNumbers?.id;
  },
  [UPDATE_DEVICE_PART_NUMBERS_SEARCH_QUERIES] : ({search}: DevicePartNumbersState, {searchParams}) => {
    search.devicePartNum           = searchParams?.devicePartNum || null;
    search.deviceType              = searchParams?.deviceType || null;
    search.deviceConfigurationName = searchParams?.deviceConfigurationName || null;
    search.carrier                 = searchParams?.carrier || null;
    search.isThirdParty            = searchParams?.isThirdParty || null;
  },
  [DELETE_DEVICE_PART_NUMBERS] : (
    {destroy}: DevicePartNumbersState,
    {devicePartNumbers, showDialog}: DevicePartNumbersAction) => {
    destroy.selected   = showDialog ? devicePartNumbers : null;
    destroy.showDialog = showDialog;
  },
  [RESET_DEVICE_PART_NUMBERS_SEARCH_QUERIES] : draft => {
    draft.search = clearSearch();
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: DevicePartNumbersState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
